/** DOCUMENTUL CU O VARIANTĂ SCRISĂ, îmbinată din răspunsul aplicării directe.
 *
 *  «Aplică varianta» întoarce exact ce s-a schimbat în document (`api.DeltaAplicata`:
 *  id-urile plecate, obiectele venite, apartamentul). Îmbinate aici peste documentul de
 *  pe ecran, ele sunt chiar obiectele scrise — aceleași id-uri, aceeași geometrie — deci
 *  planul arată varianta scrisă pe loc, fără să mai citească documentul întreg (zeci de
 *  MB pe un ansamblu; Cătălin, 30.10.2026: «mă aștept să-mi arate în UI imediat»).
 *
 *  Obiectele vin în cadrul DOCUMENTULUI, neorientate; documentul de pe ecran a trecut prin
 *  `northUp`. Completarea trece prin aceeași transformare, ca `memorie.lipesteFelia`.
 */
import type { DeltaAplicata } from "../../api";
import { northUp, type CanonicalDoc, type CanonicalObject } from "../canonical";

export function docCuDelta(doc: CanonicalDoc, aplicate: readonly DeltaAplicata[]): CanonicalDoc {
  if (!aplicate.length) return doc;
  const crs = (doc as { crs?: unknown }).crs;
  const pleaca = new Set<string>();
  const vin: CanonicalObject[] = [];
  const flaturi = new Map<string, DeltaAplicata["flat"]>();
  for (const a of aplicate) {
    for (const id of a.sterge) pleaca.add(id);
    vin.push(...(a.adauga as unknown as CanonicalObject[]));
    if (a.flat) flaturi.set(a.flat_id, a.flat);
  }
  // ORIENTATE ca documentul: obiectele noi și conturul fiecărui apartament (dus ca un
  // obiect prin aceeași transformare, ca să nu existe două feluri de a orienta un poligon)
  const orientate = northUp({ crs, objects: [
    ...vin,
    ...[...flaturi].map(([id, f]) => ({ id, type: "Flat", properties: {}, geometry: f?.geometry ?? {} })),
  ] }).objects as CanonicalObject[];
  const noi = orientate.slice(0, vin.length);
  const contur = new Map<string, Record<string, unknown>>();
  for (const o of orientate.slice(vin.length)) contur.set(o.id, o.geometry ?? {});
  const ramase = (doc.objects ?? []).filter((o) => !pleaca.has(o.id)).map((o) => {
    const f = o.type === "Flat" ? flaturi.get(o.id) : undefined;
    if (!f) return o;
    return { ...o, geometry: contur.get(o.id) ?? o.geometry, props: f.props,
             display: Object.keys(f.display ?? {}).length ? f.display : o.display };
  });
  return { ...doc, objects: [...ramase, ...noi] };
}
